import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Upload, FileImage, Trash2, Eye, Check, X } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'

interface MeetingNotesScreenshotsProps {
  meetingId: string
  isEditing?: boolean
  onSave?: () => void
  onCancel?: () => void
}

interface Screenshot {
  id: string
  filename: string
  storage_path: string
  file_size: number
  content_type: string
  uploaded_at: string
  url?: string
}

interface MeetingNotes {
  id: string
  notes: string
}

export const MeetingNotesScreenshots = ({ 
  meetingId, 
  isEditing = false, 
  onSave, 
  onCancel 
}: MeetingNotesScreenshotsProps) => {
  const { toast } = useToast()
  const [notes, setNotes] = useState('')
  const [screenshots, setScreenshots] = useState<Screenshot[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editingNotes, setEditingNotes] = useState(isEditing)
  const [originalNotes, setOriginalNotes] = useState('')

  useEffect(() => {
    loadMeetingData()
  }, [meetingId])

  const loadMeetingData = async () => {
    try {
      // Note: meeting_notes and meeting_screenshots tables don't exist
      // Using placeholder data instead
      console.log('Note: meeting_notes and meeting_screenshots tables not available')
      
      // Mock notes data
      setNotes('')
      setOriginalNotes('')

      // Mock screenshots data - empty array
      setScreenshots([])
    } catch (error) {
      console.error('Error loading meeting data:', error)
      toast({
        title: "Error",
        description: "Failed to load meeting data",
        variant: "destructive"
      })
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setIsUploading(true)

    try {
      for (const file of files) {
        const fileExt = file.name.split('.').pop()
        const fileName = `${meetingId}/${Date.now()}.${fileExt}`

        // Upload file to storage
        const { error: uploadError } = await supabase.storage
          .from('meeting-screenshots')
          .upload(fileName, file)

        if (uploadError) {
          throw uploadError
        }

        // Save metadata to database - disabled as meeting_screenshots table doesn't exist
        // Using mock metadata instead
        const newScreenshot = {
          id: Date.now().toString(), // Mock ID
          meeting_id: meetingId,
          filename: file.name,
          storage_path: fileName,
          file_size: file.size,
          content_type: file.type,
          uploaded_at: new Date().toISOString(),
          url: URL.createObjectURL(file) // Use blob URL for immediate display
        }

        setScreenshots(prev => [newScreenshot, ...prev])
      }

      toast({
        title: "Success",
        description: `${files.length} screenshot(s) uploaded successfully`
      })
    } catch (error) {
      console.error('Error uploading screenshot:', error)
      toast({
        title: "Error",
        description: "Failed to upload screenshot",
        variant: "destructive"
      })
    } finally {
      setIsUploading(false)
      // Clear the input
      event.target.value = ''
    }
  }

  const handleDeleteScreenshot = async (screenshot: Screenshot) => {
    try {
      // Delete screenshot functionality disabled - storage and table don't exist
      // Just remove from local state
      setScreenshots(prev => prev.filter(s => s.id !== screenshot.id))

      toast({
        title: "Success",
        description: "Screenshot deleted successfully"
      })
    } catch (error) {
      console.error('Error deleting screenshot:', error)
      toast({
        title: "Error",
        description: "Failed to delete screenshot",
        variant: "destructive"
      })
    }
  }

  const handleSaveNotes = async () => {
    setIsSaving(true)

    try {
      // Notes saving disabled - meeting_notes table doesn't exist
      // Just update local state
      setOriginalNotes(notes)
      console.log('Notes saved locally (database table not available)')

      setOriginalNotes(notes)
      setEditingNotes(false)

      toast({
        title: "Success",
        description: "Meeting notes saved successfully"
      })

      onSave?.()
    } catch (error) {
      console.error('Error saving notes:', error)
      toast({
        title: "Error",
        description: "Failed to save meeting notes",
        variant: "destructive"
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setNotes(originalNotes)
    setEditingNotes(false)
    onCancel?.()
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const openImageInNewTab = (url: string) => {
    window.open(url, '_blank')
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileImage className="w-5 h-5" />
          Meeting Notes & Screenshots
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Meeting Notes Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Meeting Notes</h3>
            {!editingNotes && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingNotes(true)}
              >
                Edit Notes
              </Button>
            )}
          </div>

          {editingNotes ? (
            <div className="space-y-4">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add meeting notes... You can also upload screenshots above."
                className="min-h-32"
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveNotes}
                  disabled={isSaving}
                  size="sm"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  size="sm"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-muted/50 p-4 rounded-lg min-h-32">
              {notes ? (
                <p className="whitespace-pre-wrap">{notes}</p>
              ) : (
                <p className="text-muted-foreground italic">
                  Click to add notes and screenshots...
                </p>
              )}
            </div>
          )}
        </div>

        {/* Screenshots Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Screenshots</h3>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                id="screenshot-upload"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('screenshot-upload')?.click()}
                disabled={isUploading}
              >
                <Upload className="w-4 h-4 mr-2" />
                {isUploading ? 'Uploading...' : 'Add Screenshot'}
              </Button>
            </div>
          </div>

          {screenshots.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {screenshots.map((screenshot) => (
                <div
                  key={screenshot.id}
                  className="border rounded-lg p-4 space-y-3"
                >
                  {screenshot.url && (
                    <div className="relative aspect-video bg-muted rounded overflow-hidden">
                      <img
                        src={screenshot.url}
                        alt={screenshot.filename}
                        className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => openImageInNewTab(screenshot.url!)}
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium truncate" title={screenshot.filename}>
                      {screenshot.filename}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary" className="text-xs">
                        {formatFileSize(screenshot.file_size)}
                      </Badge>
                      <div className="flex gap-1">
                        {screenshot.url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openImageInNewTab(screenshot.url!)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteScreenshot(screenshot)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground">
                      Uploaded {new Date(screenshot.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border-2 border-dashed border-muted rounded-lg">
              <FileImage className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No screenshots uploaded yet
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Add Screenshot" to upload images
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}